import React, { useState } from "react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { ScrollArea } from "../../ui/scroll-area";
import { Separator } from "../../ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../../ui/sheet";
import { 
  Bell, 
  BellOff, 
  UserPlus, 
  ArrowRight,
  CalendarCheck,
  CalendarClock,
  ExternalLink,
  Trash2,
  Check
} from "lucide-react";

import "./NotificationPanel.css";

const NotificationPanel = ({
  notifications = [],
  _setNotifications,
  loading = false,
  onNotificationClick,
  onClearAll,
  onMarkAsRead,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loadingClear, setLoadingClear] = useState(false);

  const unreadCount = notifications?.length > 0 
    ? notifications.reduce((acc, item) => (item.readed === false ? acc + 1 : acc), 0)
    : 0;

  const handleNotificationTitle = (title, name) => {
    switch (title) {
      case "Novo Lead Recebido":
        return (
          <>
            <strong>{name}</strong> agora é sua Lead. Veja o resumo e classificação de interesse.
          </>
        );
      default:
        return title;
    }
  };

  const handleNotificationClick = async (notification) => {
    if (onNotificationClick) {
      await onNotificationClick(notification);
    }
  };

  const handleClearAllNotifications = async () => {
    setLoadingClear(true);
    try {
      if (onClearAll) {
        await onClearAll();
      }
    } catch (error) {
      console.error("Error clearing notifications:", error);
    } finally {
      setLoadingClear(false);
    }
  };

  const handleMarkAsRead = async (notification, event) => {
    event.stopPropagation(); // Prevent card click when marking as read
    if (onMarkAsRead) {
      await onMarkAsRead(notification);
    }
  };

  const getNotificationIcon = (title) => {
    switch (title) {
      case "Novo Lead Recebido":
        return <UserPlus className="w-5 h-5 text-blue-600" />;
      default:
        // Aviso de agendamento à equipe (sac_gpt_ruby #213).
        if (title?.startsWith("Novo agendamento:")) return <CalendarCheck className="w-5 h-5 text-blue-600" />;
        if (title?.startsWith("Pedido de agendamento:")) return <CalendarClock className="w-5 h-5 text-amber-600" />;
        return <Bell className="w-5 h-5 text-blue-600" />;
    }
  };

  const NotificationCard = ({ notification }) => (
    <div
      className="notification-card"
      onClick={() => handleNotificationClick(notification)}
    >
      <div className="notification-card-icon">
        {getNotificationIcon(notification.title)}
      </div>
      
      <div className="notification-card-content">
        <div className="notification-card-text">
          {handleNotificationTitle(notification.title, notification.clientName)}
        </div>
      </div>
      
      <div className="notification-card-actions">
        {!notification.readed && (
          <Button
            variant="ghost"
            size="icon"
            className="notification-mark-read-button"
            onClick={(e) => handleMarkAsRead(notification, e)}
            title="Marcar como lida"
          >
            <Check className="w-4 h-4 text-gray-500 hover:text-green-600" />
          </Button>
        )}
        {notification.title === "Novo Lead Recebido" && (
          <div className="notification-card-arrow">
            <ArrowRight className="w-4 h-4 text-gray-400" />
          </div>
        )}
        {notification.title !== "Novo Lead Recebido" && notification.eventLink && (
          <div className="notification-card-arrow" title="Abrir o evento no Google">
            <ExternalLink className="w-4 h-4 text-gray-400" />
          </div>
        )}
      </div>
    </div>
  );

  const EmptyState = () => (
    <div className="notification-empty-state">
      <BellOff className="w-12 h-12 text-gray-300 mb-4" />
      <h3 className="notification-empty-title">
        Nenhuma notificação
      </h3>
      <p className="notification-empty-description">
        Você está em dia! Não há notificações pendentes.
      </p>
    </div>
  );

  const LoadingState = () => (
    <div className="notification-loading">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="notification-skeleton">
          <div className="notification-skeleton-icon" />
          <div className="notification-skeleton-content">
            <div className="notification-skeleton-line notification-skeleton-line-long" />
            <div className="notification-skeleton-line notification-skeleton-line-short" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="notification-trigger"
          aria-label={unreadCount > 0 ? `Notificações, ${unreadCount} não lidas` : "Notificações"}
        >
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="notification-badge"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </Badge>
          )}
          <Bell className="w-5 h-5" />
        </Button>
      </SheetTrigger>

      <SheetContent 
        side="right" 
        className="notification-panel"
      >
        <SheetHeader className="notification-header">
          <div className="notification-header-content">
            <div>
              <SheetTitle className="notification-title">
                Notificações
              </SheetTitle>
              <SheetDescription className="notification-description">
                {unreadCount > 0 
                  ? `${unreadCount} ${unreadCount === 1 ? 'nova notificação' : 'novas notificações'}`
                  : 'Todas as suas notificações'
                }
              </SheetDescription>
            </div>
            
            {notifications && notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAllNotifications}
                disabled={loadingClear}
                className="notification-clear-button"
              >
                {loadingClear ? (
                  <>
                    <div className="notification-spinner" />
                    Limpando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Limpar todas
                  </>
                )}
              </Button>
            )}
          </div>
          <Separator className="mt-4" />
        </SheetHeader>

        <ScrollArea className="notification-scroll-area">
          <div className="notification-list">
            {loading ? (
              <LoadingState />
            ) : notifications && notifications.length > 0 ? (
              notifications.map((notification, index) => (
                <NotificationCard 
                  key={notification.notificationId || index} 
                  notification={notification} 
                />
              ))
            ) : (
              <EmptyState />
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default NotificationPanel;